import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import MainSidebar from '../../components/ui/MainSidebar';
import { automationRulesService } from '../../services/automationRulesService';
import RuleLibrary from './components/RuleLibrary';
import RuleBuilder from './components/RuleBuilder';
import RuleTestPanel from './components/RuleTestPanel';
import ExecutionLogsPanel from './components/ExecutionLogsPanel';
import { trackAutomationRuleEvent, trackUserBehaviorFunnel, trackCampaignGoal } from '../../utils/analytics';

const AutomationRulesManagement = () => {
  const [rules, setRules] = useState([]);
  const [selectedRule, setSelectedRule] = useState(null);
  const [activeView, setActiveView] = useState('library');
  const [filters, setFilters] = useState({ status: 'active', triggerType: '' });
  const [loading, setLoading] = useState(true);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [showExecutionLogs, setShowExecutionLogs] = useState(false);

  useEffect(() => {
    loadRules();
  }, [filters]);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await automationRulesService?.getAllRules(filters);
      setRules(data || []);
    } catch (error) {
      console.error('Error loading automation rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = () => {
    trackUserBehaviorFunnel('rule_creation_started', {
      funnel_stage: 'create_automation_rule'
    });
    setSelectedRule(null);
    setActiveView('builder');
  };

  const handleEditRule = (rule) => {
    trackAutomationRuleEvent('rule_edit_started', {
      rule_id: rule?.id,
      rule_name: rule?.ruleName,
      trigger_type: rule?.triggerType
    });
    setSelectedRule(rule);
    setActiveView('builder');
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this automation rule?')) return;
    
    try {
      await automationRulesService?.deleteRule(ruleId);
      trackAutomationRuleEvent('rule_deleted', {
        rule_id: ruleId
      });
      await loadRules();
    } catch (error) {
      console.error('Error deleting rule:', error);
      alert('Failed to delete automation rule');
    }
  };

  const handleToggleStatus = async (ruleId, isActive) => {
    try {
      await automationRulesService?.toggleRuleStatus(ruleId, isActive);
      trackAutomationRuleEvent('rule_status_toggled', {
        rule_id: ruleId,
        is_active: isActive
      });
      await loadRules();
    } catch (error) {
      console.error('Error toggling rule status:', error);
      alert('Failed to update rule status');
    }
  };

  const handleTestRule = (rule) => {
    trackUserBehaviorFunnel('rule_testing_started', {
      funnel_stage: 'test_automation_rule',
      rule_id: rule?.id,
      rule_name: rule?.ruleName
    });
    setSelectedRule(rule);
    setShowTestPanel(true);
  };

  const handleViewLogs = (rule) => {
    trackAutomationRuleEvent('execution_logs_viewed', {
      rule_id: rule?.id,
      rule_name: rule?.ruleName
    });
    setSelectedRule(rule);
    setShowExecutionLogs(true);
  };

  const handleSaveRule = async () => {
    trackCampaignGoal('automation_rule_saved', {
      goal_type: 'rule_creation'
    });
    await loadRules();
    setActiveView('library');
    setSelectedRule(null);
  };

  const handleCancelEdit = () => {
    setActiveView('library');
    setSelectedRule(null);
  };

  return (
    <div className="flex h-screen bg-background">
      <MainSidebar />
      <div className="flex-1 overflow-auto ml-0 md:ml-64">
        <div className="p-4 md:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 md:mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Automation Rules Management
              </h1>
              <p className="text-muted-foreground">
                Create and manage email automation workflows with condition-based triggers
              </p>
            </div>
            {activeView === 'library' && (
              <button
                onClick={handleCreateRule}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth press-effect focus-ring"
              >
                <Icon name="Plus" size={20} />
                <span>New Rule</span>
              </button>
            )}
          </div>

          {activeView === 'library' && (
            <RuleLibrary
              rules={rules}
              loading={loading}
              filters={filters}
              onFilterChange={setFilters}
              onEdit={handleEditRule}
              onDelete={handleDeleteRule}
              onToggleStatus={handleToggleStatus}
              onTest={handleTestRule}
              onViewLogs={handleViewLogs}
            />
          )}

          {activeView === 'builder' && (
            <RuleBuilder
              rule={selectedRule}
              onSave={handleSaveRule}
              onCancel={handleCancelEdit}
            />
          )}
        </div>
      </div>

      {showTestPanel && selectedRule && (
        <RuleTestPanel
          rule={selectedRule}
          onClose={() => setShowTestPanel(false)}
        />
      )}

      {showExecutionLogs && selectedRule && (
        <ExecutionLogsPanel
          rule={selectedRule}
          onClose={() => setShowExecutionLogs(false)}
        />
      )}
    </div>
  );
};

export default AutomationRulesManagement;